import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { mainConfig } from '@config/main.config';
import { Role } from '@models/role.model';
import { User } from '@models/user.model';
import { RolesService } from '@services/roles.service';
import { UsersService } from '@services/users.service';
import { UtilsService } from '@services/utils.service';
import { iniziali } from '@services/iniziali.util';
import { PageRequest } from '@sinapsys/ngx-crud';
import { ConfermaService } from '@shared/conferma/conferma-dialog.component';
import { ColumnDefinition } from '@shared/data-table/column-definition';
import { DataTableComponent } from '@shared/data-table/data-table.component';
import { dataOraPiena, tempoRelativo } from '@shared/utils/tempo-relativo.util';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { PageComponent } from '../../../layout/page/page.component';

@Component({
  standalone: true,
  imports: [DataTableComponent, PageComponent, mainConfig.material, mainConfig.formModules],
  selector: 'app-users',
  templateUrl: './users.component.html',
})
export class UsersComponent implements OnInit, OnDestroy {
  @ViewChild(DataTableComponent, { static: false }) table!: DataTableComponent<User>;
  @ViewChild('detailsModal', { static: true }) detailsModal!: TemplateRef<any>;
  @ViewChild('personaCella', { static: true }) personaCella!: TemplateRef<any>;
  @ViewChild('ruoloCella', { static: true }) ruoloCella!: TemplateRef<any>;
  @ViewChild('accessoCella', { static: true }) accessoCella!: TemplateRef<any>;
  @ViewChild('statoCella', { static: true }) statoCella!: TemplateRef<any>;
  @ViewChild('azioniCella', { static: true }) azioniCella!: TemplateRef<any>;
  @ViewChild('fileInput') fileInput: ElementRef | undefined;

  columnDefinitions: ColumnDefinition<User>[] = [];
  searchSubscription?: Subscription;

  readonly filtroTesto = new FormControl<string>('', { nonNullable: true });
  readonly filtroRuolo = new FormControl<number | null>(null);
  readonly filtroAttivi = new FormControl<string | null>(null);

  readonly caricamento = signal(false);
  readonly errore = signal<string | null>(null);
  readonly totale = signal<number | null>(null);

  editItemForm: FormGroup;
  editItem?: User;
  dialogRef?: MatDialogRef<any>;

  rolesDataSource: Role[] = [];

  // Avatar
  imageName = signal('');
  fileSize = signal(0);
  uploadProgress = signal(0);
  imagePreview = signal('');
  selectedFile: File | null = null;
  uploadSuccess: boolean = false;
  uploadError: boolean = false;

  private readonly sottoscrizioni = new Subscription();

  constructor(
    public utilsService: UtilsService,
    private usersService: UsersService,
    private rolesService: RolesService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private conferma: ConfermaService
  ) {
    this.editItemForm = new FormGroup({
      id: new FormControl<number | undefined>(undefined),
      blogger: new FormControl<string | undefined>(undefined),
      email: new FormControl<string | undefined>(undefined),
      is_activated: new FormControl<boolean | undefined>(undefined),
      role: new FormControl<Role | undefined>(undefined),
      avatar: new FormControl<string | undefined>(undefined),
    });
  }

  ngOnDestroy(): void {
    this.sottoscrizioni.unsubscribe();
  }

  ngOnInit(): void {
    this.rolesService.search({}, new PageRequest(0, -1)).subscribe((res) => {
      this.rolesDataSource = res.items.map((r: any) => new Role(r));
    });

    this.columnDefinitions = [
      { id: 'blogger', header: 'Persona', sortable: true, template: this.personaCella },
      { id: 'role', header: 'Ruolo', sortable: false, template: this.ruoloCella, width: '170px' },
      { id: 'last_login', header: 'Ultimo accesso', sortable: true, template: this.accessoCella, width: '150px' },
      { id: 'is_activated', header: 'Stato', sortable: true, template: this.statoCella, width: '110px' },
      { id: 'id', header: 'ID', sortable: true, width: '90px', nascondiSuMobile: true },
      { id: '_', header: '', template: this.azioniCella, width: '56px', allineamento: 'fine' },
    ];

    this.sottoscrizioni.add(
      this.filtroTesto.valueChanges.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => this.search())
    );
    this.sottoscrizioni.add(this.filtroRuolo.valueChanges.subscribe(() => this.search()));
    this.sottoscrizioni.add(this.filtroAttivi.valueChanges.subscribe(() => this.search()));
  }

  search(mantieniPagina?: boolean) {
    if (!this.table) return;

    if (!this.table.sort.active) {
      this.table.sort.active = 'last_login';
      this.table.sort.direction = 'desc';
    }
    if (!mantieniPagina) this.table.paginator.pageIndex = 0;

    const q: Record<string, unknown> = {};

    // Una casella cerca in nome ed email: prima erano tre campi separati (ID,
    // nome, email) e bisognava indovinare in quale scrivere.
    const cercato = this.filtroTesto.value.trim();
    if (cercato) {
      q['$or'] = { blogger: { $like: cercato }, email: { $like: cercato } };
    }
    if (this.filtroRuolo.value) q['roles'] = { id: this.filtroRuolo.value };
    if (this.filtroAttivi.value !== null) q['is_activated'] = this.filtroAttivi.value === 'si';

    const pageRequest = new PageRequest(
      this.table.paginator.pageIndex,
      this.table.paginator.pageSize,
      this.table.sort.direction,
      this.table.sort.active
    );

    this.searchSubscription?.unsubscribe();
    this.caricamento.set(true);
    this.errore.set(null);

    this.searchSubscription = this.usersService.search(q, pageRequest).subscribe({
      next: (res) => {
        res.items = res.items.map((item) => new User(item));
        this.table.populate(res);
        this.totale.set(res.total);
        this.caricamento.set(false);
      },
      error: (err) => {
        this.caricamento.set(false);
        this.totale.set(null);
        this.errore.set(err?.error?.message || err?.error?.messaggio || err?.message || 'Errore inatteso');
        this.utilsService.defaultHttpErrorHandler(err);
      },
    });
  }

  filtriAttivi(): boolean {
    return !!this.filtroTesto.value.trim() || !!this.filtroRuolo.value || this.filtroAttivi.value !== null;
  }

  azzeraFiltri(): void {
    this.filtroTesto.setValue('', { emitEvent: false });
    this.filtroRuolo.setValue(null, { emitEvent: false });
    this.filtroAttivi.setValue(null, { emitEvent: false });
    this.search();
  }

  sottotitolo(): string {
    const totale = this.totale();
    if (totale === null) return '';
    if (totale === 0) return this.filtriAttivi() ? 'Nessuna persona con questi filtri' : 'Nessuna persona';
    return totale === 1 ? '1 persona' : `${totale} persone`;
  }

  // --- Contenuto delle celle ------------------------------------------------

  inizialiDi(utente: User): string {
    return iniziali(utente.blogger);
  }

  quando(valore: Date | string | null | undefined): string {
    if (!valore) return 'mai';
    return tempoRelativo(Math.floor(new Date(valore).getTime() / 1000));
  }

  quandoPreciso(valore: Date | string | null | undefined): string {
    if (!valore) return '';
    return dataOraPiena(Math.floor(new Date(valore).getTime() / 1000));
  }

  // --- Modifica -------------------------------------------------------------

  new() {
    this.resetAvatar();
    this.edit(new User());
  }

  edit(item: User) {
    this.editItemForm.reset();
    this.editItemForm.patchValue(item);
    this.editItem = item;

    if (item.avatar) {
      this.imageName.set(item.blogger || 'avatar.jpg');
      this.imagePreview.set(item.avatar);
      this.uploadSuccess = true;
    } else {
      this.resetAvatar();
    }

    this.dialogRef = this.dialog.open(this.detailsModal, { width: '560px' });
  }

  save() {
    const raw = this.editItemForm.getRawValue();
    raw.roles = [raw.role];
    delete raw.role;
    this.usersService.save(raw).subscribe({
      next: () => {
        this.utilsService.notificationsService.showNotification('', 'Utente salvato', 'success');
        this.search(true);
        this.dialogRef?.close();
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }

  async elimina() {
    const nome = this.editItemForm.controls['blogger'].value ?? '';
    const procedi = await this.conferma.chiedi({
      titolo: 'Eliminare questa persona?',
      testo: `"${nome}" non potrà più entrare nel pannello. Gli articoli che ha firmato restano.`,
      conferma: 'Elimina',
      distruttiva: true,
    });
    if (!procedi) return;

    this.usersService.delete(this.editItemForm.controls['id'].value).subscribe({
      next: () => {
        this.utilsService.notificationsService.showNotification('', 'Utente eliminato', 'success');
        this.search(true);
        this.dialogRef?.close();
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }

  // --- Avatar ---------------------------------------------------------------

  onFileChange(event: any): void {
    const file = event.target.files[0] as File | null;
    this.uploadFile(file);
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0] as File | null;
    this.uploadFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  uploadFile(file: File | null): void {
    if (!file || !file.type.startsWith('image/')) {
      this.uploadSuccess = false;
      this.uploadError = true;
      this.snackBar.open('Puoi inserire solo immagini', 'Chiudi', { duration: 3000, panelClass: 'error' });
      return;
    }

    this.selectedFile = file;
    this.fileSize.set(Math.round(file.size / 1024));
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Img = e.target?.result as string;
      this.imagePreview.set(base64Img);

      this.usersService.uploadAvatar(base64Img, file.name).subscribe({
        next: (response) => {
          // Nel form va l'indirizzo, non il base64.
          this.editItemForm.controls['avatar'].setValue(response.url);
          this.utilsService.notificationsService.showNotification('', 'Avatar caricato', 'success');
          this.uploadSuccess = true;
          this.uploadError = false;
        },
        error: () => {
          this.snackBar.open("Errore durante il caricamento dell'avatar", 'Chiudi', {
            duration: 3000,
            panelClass: 'error',
          });
          this.uploadSuccess = false;
          this.uploadError = true;
        },
      });
    };
    reader.readAsDataURL(file);
    this.imageName.set(file.name);
  }

  removeImage(): void {
    this.resetAvatar();
    this.editItemForm.controls['avatar'].setValue(null);
  }

  private resetAvatar(): void {
    this.selectedFile = null;
    this.imageName.set('');
    this.fileSize.set(0);
    this.imagePreview.set('');
    this.uploadSuccess = false;
    this.uploadError = false;
    this.uploadProgress.set(0);
  }
}
